import { useEffect, useMemo, useState } from 'react'

import { Modal, Field } from './AdminApp.jsx'
import {
  addPhotoAdmin,
  approveAllPendingPhotos,
  deletePhoto,
  rejectAllPendingPhotos,
  removePhotoAdmin,
  setPhotoFeatured,
  setPhotoSettings,
  setPhotoStatus,
  setPhotoSubmissionsEnabled,
  submitPhoto,
} from '../lib/data.js'
import { lastNames } from '../lib/teamName.js'

// Admin Photos tab — three sections in one screen.
//
//   Settings: enable/disable, max-per-guest, photo admin roster
//   Review Queue: pending photos with Approve / Reject (and bulk)
//   Live Wall: grid of approved photos (admin can pull any down)
//
// Photos data is read from `tournament.photos` (already streaming via the
// parent's onValue subscription) so the wall updates live as guests
// submit and admins approve.
export default function PhotosTab({ tournamentId, tournament }) {
  const settings = tournament?.photoSettings || {}
  const photoAdmins = tournament?.photoAdmins || {}
  const photos = tournament?.photos || {}
  const enabled = settings.enabled !== false
  const maxPerGuest = Number(settings.maxPerGuest) || 5
  const videoUnmuted = !!settings.videoUnmuted
  const aiModeration = settings.aiModeration !== false // default ON
  const aiPicks = settings.aiPicks === true // default OFF (opt-in Vision cost)

  const photoEntries = useMemo(
    () => Object.entries(photos).map(([id, v]) => ({ id, ...v })),
    [photos],
  )
  const pending = useMemo(
    () => photoEntries.filter((p) => p.status === 'pending').sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)),
    [photoEntries],
  )
  const approved = useMemo(
    () => photoEntries.filter((p) => p.status === 'approved').sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)),
    [photoEntries],
  )
  // teamId -> team name, so a flagged scorecard-player video shows WHO sent it
  // (lets a moderator block a bad actor).
  const teamMap = useMemo(() => {
    const m = {}
    for (const f of Object.values(tournament?.flights || {})) {
      for (const [tid, t] of Object.entries(f?.teams || {})) m[tid] = lastNames(t?.info?.name) || tid
    }
    return m
  }, [tournament])

  return (
    <div className="space-y-4">
      <SettingsCard
        tournamentId={tournamentId}
        enabled={enabled}
        maxPerGuest={maxPerGuest}
        videoUnmuted={videoUnmuted}
        aiModeration={aiModeration}
        aiPicks={aiPicks}
        photoAdmins={photoAdmins}
      />
      <ReviewQueueCard
        tournamentId={tournamentId}
        pending={pending}
        teamMap={teamMap}
      />
      <LiveWallCard
        tournamentId={tournamentId}
        approved={approved}
        totalCount={photoEntries.length}
      />
      <SuperAdminUploadCard tournamentId={tournamentId} />
    </div>
  )
}

// ── Section 1: Settings ────────────────────────────────────────────────────
function SettingsCard({ tournamentId, enabled, maxPerGuest, videoUnmuted, aiModeration, aiPicks, photoAdmins }) {
  const [maxDraft, setMaxDraft] = useState(maxPerGuest)
  const [adding, setAdding] = useState(false)
  const [savedAt, setSavedAt] = useState(0)

  useEffect(() => { setMaxDraft(maxPerGuest) }, [maxPerGuest])

  const adminEntries = Object.entries(photoAdmins || {}).map(([key, v]) => ({ key, ...v }))

  const saveMax = async () => {
    const n = Math.max(1, Number(maxDraft) || 1)
    await setPhotoSettings(tournamentId, { maxPerGuest: n, enabled: enabled !== false })
    setSavedAt(Date.now())
  }

  return (
    <div className="card">
      <h2 className="text-lg font-bold">⚙️ Photo Settings</h2>

      <div className="mt-3 space-y-3">
        <label className="inline-flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setPhotoSubmissionsEnabled(tournamentId, e.target.checked)}
          />
          <span className="font-bold">Enable public photo submissions</span>
          <span className={`pill ${enabled ? 'pill-green' : 'pill-gray'} text-xs`}>
            {enabled ? 'Open' : 'Closed'}
          </span>
        </label>

        {/* AI moderation first-pass. When ON, public submissions are scanned by
           Google AI before the human queue: confident-clean auto-approve to the
           wall, confident-bad hard-deleted (logged), uncertain/errors stay in
           the queue for a human. Default ON. */}
        <label className="block">
          <div className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={aiModeration}
              onChange={(e) => setPhotoSettings(tournamentId, { aiModeration: e.target.checked })}
            />
            <span className="font-bold">🤖 AI moderation (first pass)</span>
            <span className={`pill ${aiModeration ? 'pill-green' : 'pill-gray'} text-xs`}>
              {aiModeration ? 'On' : 'Off'}
            </span>
          </div>
          <p className="text-xs text-gray-500 ml-6 mt-1">
            Scans public photos/videos first. Clearly-clean auto-approve, clearly-bad
            are removed; anything uncertain still comes to you below.
          </p>
        </label>

        {/* AI Picks (Phase 3). Extends the Vision scan of already-KEPT photos
           with label/face/crop hints → a small shared quality boost for the
           Highlights reel. Separate per-photo Vision cost, so default OFF. Never
           affects moderation. */}
        <label className="block">
          <div className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={aiPicks}
              onChange={(e) => setPhotoSettings(tournamentId, { aiPicks: e.target.checked })}
            />
            <span className="font-bold">✨ AI Picks (highlight quality)</span>
            <span className={`pill ${aiPicks ? 'pill-green' : 'pill-gray'} text-xs`}>
              {aiPicks ? 'On' : 'Off'}
            </span>
          </div>
          <p className="text-xs text-gray-500 ml-6 mt-1">
            Scores approved photos for “interestingness” (faces, group shots, action) to boost
            Highlights. Extra Vision cost per photo — moderation is unaffected either way.
          </p>
        </label>

        {/* Mute / unmute videos on the TV photo wall. Default OFF — videos
           autoplay muted so they don't blare across the clubhouse. */}
        <label className="block">
          <div className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={videoUnmuted}
              onChange={(e) => setPhotoSettings(tournamentId, { videoUnmuted: e.target.checked })}
            />
            <span className="font-bold">{videoUnmuted ? '🔊' : '🔇'} Video Audio on Photo Wall TV</span>
            <span className={`pill ${videoUnmuted ? 'pill-green' : 'pill-gray'} text-xs`}>
              {videoUnmuted ? 'Sound on' : 'Muted'}
            </span>
          </div>
          <p className="text-xs text-gray-500 ml-6 mt-1">
            Videos play muted by default. Enable to play with sound.
          </p>
        </label>

        <div className="flex items-center gap-2">
          <span className="text-xs">Max submissions per guest:</span>
          <input
            type="number"
            min="1"
            className="input w-20 text-sm py-1"
            value={maxDraft}
            onChange={(e) => setMaxDraft(e.target.value)}
            onBlur={saveMax}
          />
          {savedAt ? (
            <span className="text-xs text-green-deep">Saved · {new Date(savedAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}</span>
          ) : null}
        </div>
      </div>

      <div className="mt-5">
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-bold text-sm uppercase tracking-widest text-gray-500">Photo Admins</h3>
          <button className="btn-ghost text-xs" onClick={() => setAdding(true)}>+ Add Photo Admin</button>
        </div>
        {adminEntries.length === 0 ? (
          <p className="text-sm text-gray-500 italic">
            No photo admins yet. Add one to give a club member instant-post privileges via QR.
          </p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {adminEntries.map((a) => (
              <li key={a.key} className="py-2 flex justify-between items-center gap-2">
                <div className="min-w-0">
                  <div className="font-semibold truncate flex items-center gap-2">
                    {a.name}
                    {a.canVideo ? <span className="pill pill-green text-[10px]">🎥 Video</span> : null}
                  </div>
                  <div className="text-xs text-gray-500">PIN: <span className="font-mono font-bold">{a.pin}</span></div>
                </div>
                <button
                  className="text-red-600 text-xs font-semibold"
                  onClick={() => {
                    if (!confirm(`Remove ${a.name} as a photo admin?`)) return
                    removePhotoAdmin(tournamentId, a.key)
                  }}>
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {adding ? (
        <AddPhotoAdminModal
          tournamentId={tournamentId}
          onClose={() => setAdding(false)}
        />
      ) : null}
    </div>
  )
}

function AddPhotoAdminModal({ tournamentId, onClose }) {
  const [name, setName] = useState('')
  const [canVideo, setCanVideo] = useState(false)
  const [busy, setBusy] = useState(false)
  const [created, setCreated] = useState(null)

  const submit = async (e) => {
    e.preventDefault()
    if (!name.trim() || busy) return
    setBusy(true)
    try {
      const res = await addPhotoAdmin(tournamentId, name, canVideo)
      if (res) setCreated(res)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title="Add Photo Admin" onClose={onClose}>
      {created ? (
        <div className="space-y-3 text-center">
          <p className="text-sm text-gray-700">
            <b>{created.name}</b> can now post photos directly to the wall.
          </p>
          <div className="rounded-xl bg-amber-50 border-2 border-dashed border-amber-300 p-4">
            <div className="text-xs uppercase tracking-widest text-amber-800 font-bold">PIN</div>
            <div className="text-3xl font-extrabold tracking-widest mt-1">{created.pin}</div>
            <p className="text-xs text-amber-800 mt-2">Print their personal QR from the QR Codes tab.</p>
          </div>
          <button className="btn-primary w-full" onClick={onClose}>Done</button>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3">
          <Field label="Name">
            <input autoFocus className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" className="mt-0.5" checked={canVideo} onChange={(e) => setCanVideo(e.target.checked)} />
            <span>
              <span className="font-bold">🎥 Allow video</span>
              <span className="block text-xs text-gray-500">
                Their videos post straight to the wall (AI still scans as a silent backstop). Leave off for photo-only staff.
              </span>
            </span>
          </label>
          <p className="text-xs text-gray-600">A 4-digit PIN will be generated automatically.</p>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn-primary" disabled={busy || !name.trim()}>{busy ? 'Adding…' : 'Add'}</button>
          </div>
        </form>
      )}
    </Modal>
  )
}

// ── Section 2: Review Queue ────────────────────────────────────────────────
function ReviewQueueCard({ tournamentId, pending, teamMap }) {
  const count = pending.length
  return (
    <div className="card">
      <div className="flex flex-wrap justify-between items-center gap-2">
        <h2 className="text-lg font-bold">
          📋 Review Queue
          {count > 0 ? (
            <span className="ml-2 inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-red-600 text-white text-xs font-extrabold">
              {count}
            </span>
          ) : null}
        </h2>
        {count > 0 ? (
          <div className="flex gap-2">
            <button
              className="btn-secondary text-xs"
              onClick={async () => {
                if (!confirm(`Approve all ${count} pending photo${count === 1 ? '' : 's'}?`)) return
                await approveAllPendingPhotos(tournamentId, 'Admin')
              }}>
              ✓ Approve all
            </button>
            <button
              className="text-xs text-red-600 font-bold"
              onClick={async () => {
                if (!confirm(`Reject all ${count} pending photo${count === 1 ? '' : 's'}?`)) return
                await rejectAllPendingPhotos(tournamentId)
              }}>
              ✗ Reject all
            </button>
          </div>
        ) : null}
      </div>
      {count === 0 ? (
        <p className="text-sm text-gray-500 mt-3 italic">No pending photos right now.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
          {pending.map((p) => (
            <PendingCard key={p.id} tournamentId={tournamentId} photo={p} teamMap={teamMap} />
          ))}
        </div>
      )}
    </div>
  )
}

function PendingCard({ tournamentId, photo, teamMap }) {
  const teamName = photo.teamId ? (teamMap?.[photo.teamId] || photo.teamId) : null
  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden bg-white">
      <PhotoMedia photo={photo} className="w-full h-48 object-cover bg-gray-100" />
      <div className="p-3">
        <div className="text-xs text-gray-500 flex flex-wrap gap-2">
          <span className="font-bold text-gray-700">{photo.submittedBy || 'Guest'}</span>
          <span className="pill-gray text-[10px]">{labelForRole(photo.submitterRole)}</span>
          {photo.type === 'video' ? <span className="pill-gray text-[10px]">🎥 Video</span> : null}
          {teamName ? <span className="pill-gray text-[10px]">🏷️ {teamName}</span> : null}
          {photo.hole != null ? <span className="pill-gray text-[10px]">Hole {photo.hole}</span> : null}
        </div>
        {photo.aiDecision === 'needs-review' && photo.aiReason ? (
          <div className="mt-1 inline-flex items-center gap-1 rounded-md bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-bold text-amber-800">
            🤖 AI unsure: {formatAiReason(photo.aiReason)}
          </div>
        ) : null}
        {photo.caption ? (
          <p className="text-sm mt-1 line-clamp-2">{photo.caption}</p>
        ) : null}
        <div className="text-[10px] text-gray-400 mt-1">{formatTime(photo.timestamp)}</div>
        <div className="flex gap-2 mt-2">
          <button
            className="flex-1 rounded-lg bg-green-deep text-white text-xs font-extrabold py-1.5"
            onClick={() => setPhotoStatus(tournamentId, photo.id, 'approved', 'Admin')}>
            ✓ Approve
          </button>
          <button
            className="flex-1 rounded-lg bg-red-600 text-white text-xs font-extrabold py-1.5"
            onClick={() => setPhotoStatus(tournamentId, photo.id, 'rejected', 'Admin')}>
            ✗ Reject
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Section 3: Live Wall ───────────────────────────────────────────────────
function LiveWallCard({ tournamentId, approved, totalCount }) {
  return (
    <div className="card">
      <div className="flex flex-wrap justify-between items-center gap-2">
        <h2 className="text-lg font-bold">🖼️ Live Wall</h2>
        <div className="text-xs text-gray-600">
          {approved.length} live · {totalCount} total
        </div>
      </div>
      {approved.length === 0 ? (
        <p className="text-sm text-gray-500 mt-3 italic">No approved photos yet.</p>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
          {approved.map((p) => (
            <ApprovedCard key={p.id} tournamentId={tournamentId} photo={p} />
          ))}
        </div>
      )}
    </div>
  )
}

function ApprovedCard({ tournamentId, photo }) {
  return (
    <div className="rounded-xl border border-gray-200 overflow-hidden bg-white">
      <PhotoMedia photo={photo} className="w-full h-48 object-cover bg-gray-100" />
      <div className="p-3">
        <div className="text-xs text-gray-500 flex flex-wrap gap-2">
          <span className="font-bold text-gray-700">{photo.submittedBy || 'Guest'}</span>
          {photo.hole != null ? <span className="pill-gray text-[10px]">Hole {photo.hole}</span> : null}
        </div>
        {photo.caption ? <p className="text-sm mt-1 line-clamp-2">{photo.caption}</p> : null}
        <div className="text-[10px] text-gray-400 mt-1 flex items-center gap-2">
          {formatTime(photo.timestamp)}
          {photo.featured ? <span className="pill-gold text-[10px]">★ Featured</span> : null}
          {Number(photo.likeCount) > 0 ? <span className="text-gray-500">♥ {photo.likeCount}</span> : null}
        </div>
        <div className="flex gap-2 mt-2">
          <button
            className="rounded-lg border text-xs font-bold py-1.5 px-2"
            style={photo.featured
              ? { backgroundColor: 'var(--ge-primary)', color: 'var(--ge-primary)', borderColor: 'var(--ge-primary)' }
              : { borderColor: 'var(--ge-primary)', color: '#8a6d1f' }}
            onClick={() => setPhotoFeatured(tournamentId, photo.id, !photo.featured)}>
            ★ {photo.featured ? 'Featured' : 'Feature'}
          </button>
          <button
            className="flex-1 rounded-lg border border-amber-300 text-amber-800 text-xs font-bold py-1.5"
            onClick={() => setPhotoStatus(tournamentId, photo.id, 'rejected', 'Admin')}>
            ↺ Pull
          </button>
          <button
            className="rounded-lg border border-red-300 text-red-700 text-xs font-bold py-1.5 px-2"
            onClick={() => {
              if (!confirm('Delete this photo permanently?')) return
              deletePhoto(tournamentId, photo.id, photo.storagePath)
            }}>
            Delete
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Super Admin uploader ───────────────────────────────────────────────────
// Lets the tournament admin drop their own photos straight onto the wall
// (status='approved') without going through the public submission page.
function SuperAdminUploadCard({ tournamentId }) {
  const [busy, setBusy] = useState(false)
  const [flash, setFlash] = useState(null)

  const onPick = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setBusy(true)
    try {
      await submitPhoto(tournamentId, file, {
        submitterRole: 'admin',
        submittedBy: 'Tournament Admin',
        approvedBy: 'Admin',
      })
      setFlash({ kind: 'ok', msg: '✓ Posted directly to the wall.' })
    } catch (err) {
      setFlash({ kind: 'err', msg: 'Upload failed: ' + (err?.message || String(err)) })
    } finally {
      setBusy(false)
      setTimeout(() => setFlash(null), 5000)
    }
  }

  return (
    <div className="card">
      <h2 className="text-lg font-bold">🎯 Admin Quick Post</h2>
      <p className="text-xs text-gray-500 mt-1">Your uploads bypass review and post directly to the wall.</p>
      <label className="btn-primary inline-flex items-center gap-2 mt-3 cursor-pointer">
        {busy ? 'Uploading…' : '📸 Upload Photo / Video'}
        <input
          type="file"
          accept="image/*,video/*"
          className="hidden"
          disabled={busy}
          onChange={onPick}
        />
      </label>
      {flash ? (
        <p className={`text-xs font-bold mt-2 ${flash.kind === 'ok' ? 'text-green-deep' : 'text-red-600'}`}>
          {flash.msg}
        </p>
      ) : null}
    </div>
  )
}

// ── helpers ────────────────────────────────────────────────────────────────
// Renders a photo or video with a duration badge overlay on videos so admins
// see the length without having to play the clip.
function PhotoMedia({ photo, className }) {
  if (photo.type === 'video') {
    return (
      <div className="relative">
        <video src={photo.url} className={className} controls preload="metadata" playsInline />
        {Number.isFinite(Number(photo.durationSeconds)) ? (
          <div
            className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded text-[10px] font-extrabold pointer-events-none"
            style={{ backgroundColor: 'rgba(0,0,0,0.7)', color: '#fff' }}>
            ▶ {formatDuration(photo.durationSeconds)}
          </div>
        ) : null}
      </div>
    )
  }
  return <img src={photo.thumbnailUrl || photo.url} alt={photo.caption || ''} className={className} loading="lazy" />
}

function formatDuration(seconds) {
  const total = Math.max(0, Math.round(Number(seconds) || 0))
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function labelForRole(role) {
  if (role === 'photoAdmin') return 'Photo Admin'
  if (role === 'admin') return 'Admin'
  return 'Public'
}

// Turn a stored AI reason ("racy: POSSIBLE" / "scan-failed") into short UI text.
function formatAiReason(reason) {
  if (!reason) return ''
  if (reason === 'scan-failed') return "couldn't scan — please review"
  const [cat, lik] = String(reason).split(':').map((s) => s.trim())
  return lik ? `${cat} (${lik.toLowerCase().replace(/_/g, ' ')})` : cat
}

function formatTime(ts) {
  if (!ts) return ''
  const d = new Date(ts)
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}
